<script setup>
import { computed, ref, onMounted, toRaw } from 'vue'
import { useIterableLiveBlocksMeta } from '~/composables/useIterableLiveBlocksMeta'
import { useIterable } from '~/composables/useIterable'
import {
  appendMissingLiveCardProgramRows,
  findMatchingLiveIterableBlock,
  mergeIterableDynamicZoneBlock,
  normalizePlacementIdFromPageBlock,
  numericPlacementIdFromIterableRelation,
  safeStrapiTextField,
  unwrapStrapiScalar
} from '~/utils/iterable-live-merge'

const props = defineProps({
  data: Object,
  location: Object
})
const { urlRegisterFunction } = useUtils()
const { prefetchPulseRoute } = usePulsePrefetch()
const { altFallback } = usePageAltFallback()
const getImageAltWithFallback = (media) =>
  resolveMediaAlt(media, altFallback.value, 'Image')
const { isEnabled, identityReady, hasIterableIdentity, embeddedMessagesByPlacement } = useIterable()

const sharedContent = computed(() => !!props.data?.sharedContent)

const rawCards = computed(() => {
  if (sharedContent.value) {
    if (props.data.programs?.data) {
      return props.data.programs.data.map((program) => ({
        id: program?.id,
        ...(program?.attributes ?? {})
      }))
    }
    return props.data?.programs ?? []
  }
  const cards = props.data?.cards ? [...props.data.cards] : []
  cards.forEach((cardProgramRow) => {
    if (!cardProgramRow?.locationCTAs?.verticalGallery) return
    const galleryImagesData = cardProgramRow.locationCTAs?.vgRel?.images?.data
    if (galleryImagesData) {
      const imagesArray = galleryImagesData.map((image) => ({
        id: image?.id,
        ...(image?.attributes ?? {})
      }))
      const vgRel = cardProgramRow.locationCTAs?.vgRel
      if (vgRel) {
        vgRel.images = imagesArray
      }
    }
  })
  return cards
})

/** Same ref as `useAsyncData` default — live blocks merge after client hydration. */
const { data: iterableLiveState } = useIterableLiveBlocksMeta()

const isHydrated = ref(false)

const liveIterableBlocks = computed(() =>
  !sharedContent.value && isHydrated.value ? iterableLiveState.value?.blocks ?? [] : []
)

const resolvedCards = computed(() => {
  const list = rawCards.value
  if (!Array.isArray(list)) return []
  if (sharedContent.value) return list
  const merged = list.map((card) => {
    const live = findMatchingLiveIterableBlock(card, liveIterableBlocks.value)
    return live ? mergeIterableDynamicZoneBlock({ ...card }, live) : card
  })
  /** Live-meta card rows absent from stale SSR/CDN page payload. */
  return appendMissingLiveCardProgramRows(merged, liveIterableBlocks.value)
})

function getPlacementAttr (card, key, fallback) {
  const placementRelation = card?.iterablePlacement
  const raw =
    placementRelation?.[key] ??
    placementRelation?.data?.attributes?.[key] ??
    placementRelation?.attributes?.[key]
  const coerced = safeStrapiTextField(unwrapStrapiScalar(raw), '')
  return coerced !== '' ? coerced : fallback
}

function cardPlacementNumericId (card) {
  return numericPlacementIdFromIterableRelation(card?.iterablePlacement)
}

function cardUsesIterable (card) {
  return (
    normalizePlacementIdFromPageBlock({ iterablePlacement: card?.iterablePlacement }) != null &&
    cardPlacementNumericId(card) != null
  )
}

/** Prefer Iterable only when the SDK has embedded content for this placement (aligns with `EmbeddedPlacement` `canRenderPlacement`). */
function cardShowsIterableEmbedded (card) {
  if (!cardUsesIterable(card) || !isHydrated.value) return false
  if (!isEnabled.value || !identityReady.value || !hasIterableIdentity.value) return false
  const placementNumericId = cardPlacementNumericId(card)
  if (placementNumericId == null) return false
  const messagesByPlacement =
    embeddedMessagesByPlacement?.value != null
      ? toRaw(embeddedMessagesByPlacement.value)
      : null
  if (messagesByPlacement == null || typeof messagesByPlacement !== 'object') return false
  const messagesForPlacement = messagesByPlacement[placementNumericId]
  return Array.isArray(messagesForPlacement) && messagesForPlacement.length > 0
}

/**
 * Iterable-only rows (Strapi `iterablePlacement` / **I**) appear only when `cardShowsIterableEmbedded` is true.
 * Otherwise they are omitted so the grid has no empty slot; **N** rows (no placement) always stay.
 */
const visibleCards = computed(() => {
  const list = resolvedCards.value
  if (!Array.isArray(list)) return []
  if (sharedContent.value) return list
  return list.filter((card) => !cardUsesIterable(card) || cardShowsIterableEmbedded(card))
})

const cardsNumber = computed(() => visibleCards.value.length)

const colClass = computed(() => {
  const cardCount = cardsNumber.value
  if (cardCount === 2) return 'col-md-6 card-count-2'
  if (cardCount === 3) return 'col-lg-4 col-md-6 card-count-3'
  if (cardCount === 4) return 'col-lg-3 col-md-6 card-count-4'
  return 'col-md-12'
})

const colInnerClass = computed(() => {
  const cardCount = cardsNumber.value
  if (cardCount === 3 || cardCount === 2 || cardCount === 4) return 'col-md-12'
  return 'col-md-6'
})

function cardPrimaryClass (card) {
  return (
    getPlacementAttr(card, 'primaryButtonClass', '') ||
    getPlacementAttr(card, 'button1Class', 'btn btn-primary')
  )
}

function cardSecondaryClass (card) {
  return (
    getPlacementAttr(card, 'secondaryButtonClass', '') ||
    getPlacementAttr(card, 'button2Class', 'btn btn-secondary')
  )
}

function cardTitleClass (card) {
  return getPlacementAttr(card, 'titleClass', 'heading-three')
}

function cardBodyClass (card) {
  return getPlacementAttr(card, 'bodyClass', 'para-one')
}

/**
 * Strapi v5 may send `id` as `{ documentId }`. Never use `card.id` in string concat / keys — it throws
 * `Cannot convert object to primitive value` during Vue `patchAttr` when Iterable updates re-render the grid.
 */
function cardProgramRowDomSuffix (card, index) {
  const idValue = card?.id
  if (idValue == null) return String(index)
  if (typeof idValue === 'string' || typeof idValue === 'number') return String(idValue)
  if (typeof idValue === 'object' && idValue !== null && 'documentId' in idValue) {
    const documentId = idValue.documentId
    if (typeof documentId === 'string' || typeof documentId === 'number') return String(documentId)
  }
  return String(index)
}

/** Safe `title` / `alt` / other DOM attrs — Strapi media fields may be objects or enums. */
function strapiMediaAttrText (value) {
  const mediaAttrText = safeStrapiTextField(unwrapStrapiScalar(value), '')
  return mediaAttrText !== '' ? mediaAttrText : undefined
}

// Computed property to calculate column class based on index
const getColumnClass = (index) => {
  if (index % 3 === 0) {
    // 1st, 4th, 7th, etc. images take up full width
    return 'col-12 py-4 px-4'
  } else {
    // 2nd, 3rd, 5th, 6th, 8th, 9th, etc. images share the width
    return 'col-6 d-inline-block px-4'
  }
}

const showBadge = (badgeText, locationCTAs) => {
  if (badgeText) {
    if (locationCTAs?.verticalGallery) {
      return false
    }
    return true
  }
  return false
}

/** Match desktop `goToAVC`: only positive finite numeric ids may appear in `did=` query params. */
function validPositiveDivisionId (divisionId) {
  const n = Number(divisionId)
  return Number.isFinite(n) && n > 0 ? n : null
}

function primaryCtaRegisterQuery (card) {
  const lid = props.location?.pulseId
  const did = validPositiveDivisionId(card?.locationCTAs?.divisionId)
  return did != null ? `?lid=${lid}&did=${did}` : `?lid=${lid}`
}

const goToAVC = (divisionId) => {
  const divisionIdValue = validPositiveDivisionId(divisionId)
  if (divisionIdValue != null) {
    location.href = `#/reg-flow/avail-charts-lock?lid=${props.location.pulseId}&did=${divisionIdValue}&rgnad=true`
    Pulse.app.refreshRoute();
  }

  const spaContainer = document.getElementById('www-spa-container')
  if (spaContainer != null) {
    const offset = spaContainer.offsetTop
    const deviceOffset = screen.width >= 992 ? 80 : 0
    const scrollTopTo = offset - 130 - deviceOffset
    window.scrollTo({
      top: scrollTopTo,
      behavior: 'smooth'
    })
  }
}

onMounted(() => {
  isHydrated.value = true

  if (!props.location?.pulseId || props.location?.status?.typeId !== 1) return

  const cards = visibleCards.value
  if (!Array.isArray(cards)) return

  let primaryDivisionId = null
  cards.some((card) => {
    const divisionId = validPositiveDivisionId(card?.locationCTAs?.divisionId)
    if (divisionId != null) {
      primaryDivisionId = divisionId
      return true
    }
    return false
  })

  if (!primaryDivisionId) {
    prefetchPulseRoute(`/reg-flow/avail-charts-lock?lid=${props?.location?.pulseId}&rgnad=true`)
    return
  }

  prefetchPulseRoute(`/reg-flow/avail-charts-lock?lid=${props?.location?.pulseId}&did=${primaryDivisionId}&rgnad=true`)
})
</script>

<template>
  <div class="block card-programs">
    <div class="container">
      <div class="row justify-content-center">
        <div
          :class="'col-12 ' + colClass"
          v-for="(card, index) in visibleCards"
          :key="`${cardProgramRowDomSuffix(card, index)}-${index}`"
        >
          <div class="card-program-item">
            <template v-if="cardShowsIterableEmbedded(card)">
              <ClientOnly>
                <IterableEmbeddedPlacement
                  :placement-id="cardPlacementNumericId(card)"
                  message-type="card"
                  omit-card-item-wrapper
                  :primary-button-class="cardPrimaryClass(card)"
                  :secondary-button-class="cardSecondaryClass(card)"
                  :title-class="cardTitleClass(card)"
                  :body-class="cardBodyClass(card)"
                />
                <template #fallback />
              </ClientOnly>
            </template>
            <div v-else class="row">
              <div :class="'col-12 ' + colInnerClass + ' card-image-container'">
                <div class="position-relative">
                  <NuxtImg
                    :src="card?.image?.url || 'https://via.placeholder.com/640x360'"
                    class="card-image"
                    :title="strapiMediaAttrText(card?.image?.caption)"
                    :alt="getImageAltWithFallback(card?.image)"
                    loading="lazy"
                    decoding="async"
                    format="webp"
                  />
                  <div
                    :class="'card-badge bg-' + safeStrapiTextField(unwrapStrapiScalar(card?.badgeColor), '')"
                    v-html="card?.badgeText"
                    v-if="showBadge(card?.badgeText, card?.locationCTAs)"
                  />
                  <div
                    class="btn btn-secondary card-badge"
                    data-bs-toggle="modal"
                    :data-bs-target="'#gallery-' + cardProgramRowDomSuffix(card, index)"
                    v-if="card?.locationCTAs?.verticalGallery"
                  >
                    {{ card?.locationCTAs?.vgBtnText }}
                  </div>
                </div>
                <template v-if="card?.locationCTAs?.verticalGallery">
                  <UIModal
                    :modalId="'gallery-' + cardProgramRowDomSuffix(card, index)"
                    modalClass="modal-fullscreen modal-gallery"
                    :modalFooterBtnText="card?.locationCTAs?.vgRel?.buttonText"
                    modalHeaderClass="d-none"
                    modalFooterBtnStyle="btn btn-primary"
                  >
                    <div class="container text-center">
                      <h2 class="text-start col-12">{{ card?.locationCTAs?.vgRel?.galleryTitle }}</h2>
                      <div
                        v-for="(media, galleryIndex) in (
                          Array.isArray(card?.locationCTAs?.vgRel?.images)
                            ? (card?.locationCTAs?.vgRel?.images ?? [])
                            : []
                        )"
                        :key="galleryIndex"
                        :class="getColumnClass(galleryIndex)"
                      >
                        <NuxtImg
                          :src="media?.url || 'https://via.placeholder.com/800x600'"
                          :title="strapiMediaAttrText(media?.caption)"
                          :alt="getImageAltWithFallback(media)"
                          loading="lazy"
                          decoding="async"
                          format="webp"
                          class="img-fluid"
                        />
                      </div>
                    </div>
                  </UIModal>
                </template>
              </div>
              <div :class="'col-12 ' + colInnerClass + ' card-information'">
                <div class="card-title" v-if="card?.iconOptional?.url">
                  <NuxtImg
                    :src="card?.iconOptional?.url"
                    class="card-image"
                    :title="strapiMediaAttrText(card?.iconOptional?.caption)"
                    :alt="getImageAltWithFallback(card?.iconOptional)"
                    loading="lazy"
                    decoding="async"
                    format="webp"
                  />
                </div>
                <article class="card-content" v-html="card?.description ?? ''" />
                <div
                  v-if="card?.locationCTAs?.togglePrimaryCTA && location?.status?.typeId == 1"
                  class="card-btn-container"
                >
                  <a
                    class="btn btn-primary mb-3 d-none d-md-block"
                    @click="goToAVC(card?.locationCTAs?.divisionId)"
                  >{{ card?.locationCTAs?.primaryCTA ?? 'View courses, prices & dates' }}</a>
                  <a
                    class="btn btn-primary mb-3 d-block d-md-none"
                    :href="urlRegisterFunction(primaryCtaRegisterQuery(card), true)"
                  >{{ card?.locationCTAs?.primaryCTA ?? 'View courses, prices & dates' }}</a>
                </div>
                <div v-if="card?.locationCTAs?.toggleSecondaryCTA" class="card-btn-container">
                  <NuxtLink
                    class="btn btn-outline-secondary"
                    data-bs-toggle="modal"
                    :data-bs-target="'#sample-schedule-' + cardProgramRowDomSuffix(card, index)"
                  >{{ card?.locationCTAs?.secondaryCTA ?? 'View sample schedule' }}</NuxtLink>
                  <UIModal :modalId="'sample-schedule-' + cardProgramRowDomSuffix(card, index)" modalClass="modal-sample-schedule">
                    <template v-slot:extra-title>
                      <div class="extra-title" v-if="card?.iconOptional?.url">
                        <NuxtImg
                          :src="card?.iconOptional?.url"
                          class="card-image"
                          :title="strapiMediaAttrText(card?.iconOptional?.caption)"
                          :alt="getImageAltWithFallback(card?.iconOptional)"
                          loading="lazy"
                          decoding="async"
                          format="webp"
                        />
                        <h6 class="modal-title px-2">{{ card?.locationCTAs?.modalTitle }}</h6>
                      </div>
                    </template>
                    <article v-html="card?.locationCTAs?.sampleScheduleContent ?? ''" />
                  </UIModal>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .card-programs {
    .card-program-item {
      border-radius: 24px;
      border: solid 1px $color-light-gray;
      padding: 20px;
      margin-bottom: 10px;
      background: $color-secondary;
      color: $color-body-text;

      .card-image-container {
        // position: relative;

        .card-image {
          border-radius: 12px;
          max-width: 100%;
          margin: 0 auto;
          display: block;
          // position: relative;
        }
        .card-badge {
          padding: 2px 8px;
          border-radius: 10.5px;
          position: absolute;
          bottom: 0.625rem;
          right: 1.5rem;
          z-index: 1;
        }
      }

      .card-information {
        .card-title, .card-content {
          display: block;
          margin-top: 20px;
          padding: 0 20px;
        }
        .card-title {
          font-size: 1.25rem;
          font-weight: bold;
        }
        .card-content {
          font-size: $font-size-base;

          a.btn {
            width: 100%;
          }
          p:last-child {
            margin-bottom: 0;
          }
        }

        .modal-sample-schedule {
          .modal-header {
            .extra-title {
              font-size: 1.25rem;
              font-weight: bold;
            }
          }

          .modal-body {
            padding: 0;

            ul {
              list-style: none;
              margin: 0;
              padding: 0;

              li {
                border-top: 1px solid #dedede;
                display: flex;
                align-items: center;
                padding: 5px 25px;
                font-size: 1rem;
              }
            }

            li > * {
              flex: 1 1 auto;

              &:first-child {
                flex: 0 0 25%;
                padding-left: 0;
                font-weight: bold;
              }

              &:last-child {
                padding-right: 0;
              }
            }
          }
        }
        .card-btn-container {
          a.btn {
            width: 100%;
          }
        }
      }
      .modal-gallery {
        img {
          border-radius: 24px;
        }
        .modal-footer {
          justify-content: center;
        }
      }
    }
    .card-count-3 {
      .card-program-item {
        .iterable-card-parent {
          .iterable-content {
            padding-inline: 20px;
          }
        }
      }
    }

    .card-count-4 {
      display: flex;
      flex-direction: column;
      margin-bottom: 10px;

      .card-program-item {
        flex: 1 1 auto;
        width: 100%;
        min-height: 100%;
        display: flex;
        flex-direction: column;

        > .row {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .card-image-container {
          flex: 0 0 auto;
        }

        .card-information {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          min-height: 0;

          .card-content {
            padding-inline: 0;
            flex: 1 1 auto;
            display: flex !important;
            flex-direction: column;
            min-height: 0;

            h3 {
              font-size: 20px;
              font-weight: 700;
            }

            > p:last-child {
              margin-top: auto;
            }
          }
        }

        .iterable-embedded-placement,
        .iterable-embedded-item,
        .iterable-card-parent {
          height: 100%;

          .iterable-content {
            .heading-three {
              font-size: 20px;
              font-weight: 700;
            }
          }

          .iterable-button-parent {
            margin-top: auto;
          }
        }
      }
    }
  }
</style>
